import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap, type Schemas } from './client';
import { sessionKey } from './session';

export type WorkspaceDetails = Schemas['WorkspaceDetails'];
export type WorkspaceSettings = Schemas['WorkspaceSettings'];
export type Member = Schemas['Member'];
export type UserGroup = Schemas['UserGroupDto'];
export type FeatureOverview = Schemas['FeatureOverview'];
export type Feature = Schemas['FeatureState']['feature'];

export function useWorkspaces() {
  return useQuery({
    queryKey: ['workspaces'],
    queryFn: () => unwrap(api.GET('/api/v1/workspaces')),
  });
}

export function useWorkspace(workspaceId: string | undefined) {
  return useQuery({
    queryKey: ['workspace', workspaceId],
    queryFn: () => unwrap(api.GET('/api/v1/workspaces/{workspaceId}', { params: { path: { workspaceId: workspaceId! } } })),
    enabled: !!workspaceId,
  });
}

export function useFeatures(workspaceId: string | undefined) {
  return useQuery({
    queryKey: ['workspace', workspaceId, 'features'],
    queryFn: () =>
      unwrap(api.GET('/api/v1/workspaces/{workspaceId}/features', { params: { path: { workspaceId: workspaceId! } } })),
    enabled: !!workspaceId,
  });
}

export function useUpdateFeatures(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (changes: Partial<Record<Feature, boolean>>) =>
      unwrap(
        api.PATCH('/api/v1/workspaces/{workspaceId}/features', {
          params: { path: { workspaceId } },
          body: changes as Record<string, boolean>,
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workspace', workspaceId, 'features'] }),
  });
}

export function useCreateWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => unwrap(api.POST('/api/v1/workspaces', { body: { name } })),
    onSuccess: () => qc.invalidateQueries(),
  });
}

export function useActivateWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (workspaceId: string) =>
      unwrap(api.POST('/api/v1/workspaces/{workspaceId}/activate', { params: { path: { workspaceId } } })),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: sessionKey });
      await qc.invalidateQueries({ queryKey: ['workspaces'] });
    },
  });
}

export function useUpdateWorkspace(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Schemas['UpdateWorkspaceRequest']) =>
      unwrap(api.PATCH('/api/v1/workspaces/{workspaceId}', { params: { path: { workspaceId } }, body })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['workspace', workspaceId] });
      void qc.invalidateQueries({ queryKey: ['workspaces'] });
    },
  });
}

export function useDeleteWorkspace(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.DELETE('/api/v1/workspaces/{workspaceId}', { params: { path: { workspaceId } } })),
    onSuccess: () => qc.invalidateQueries(),
  });
}

export function useMembers(workspaceId: string) {
  return useQuery({
    queryKey: ['workspace', workspaceId, 'members'],
    queryFn: () =>
      unwrap(api.GET('/api/v1/workspaces/{workspaceId}/members', { params: { path: { workspaceId } } })),
  });
}

export function useInviteMembers(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Schemas['InviteRequest']) =>
      unwrap(api.POST('/api/v1/workspaces/{workspaceId}/members/invite', { params: { path: { workspaceId } }, body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workspace', workspaceId, 'members'] }),
  });
}

export function useUpdateMember(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ membershipId, ...body }: Schemas['UpdateMemberRequest'] & { membershipId: string }) =>
      unwrap(
        api.PATCH('/api/v1/workspaces/{workspaceId}/members/{membershipId}', {
          params: { path: { workspaceId, membershipId } },
          body,
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['workspace', workspaceId, 'members'] });
      void qc.invalidateQueries({ queryKey: ['workspace', workspaceId, 'groups'] });
    },
  });
}

export function useRemoveMember(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (membershipId: string) =>
      unwrap(
        api.DELETE('/api/v1/workspaces/{workspaceId}/members/{membershipId}', {
          params: { path: { workspaceId, membershipId } },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workspace', workspaceId, 'members'] }),
  });
}

export function useResendInvitation(workspaceId: string) {
  return useMutation({
    mutationFn: (membershipId: string) =>
      unwrap(
        api.POST('/api/v1/workspaces/{workspaceId}/members/{membershipId}/resend-invitation', {
          params: { path: { workspaceId, membershipId } },
        }),
      ),
  });
}

export function useTransferOwnership(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (membershipId: string) =>
      unwrap(
        api.POST('/api/v1/workspaces/{workspaceId}/transfer-ownership', {
          params: { path: { workspaceId } },
          body: { membershipId },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workspace', workspaceId] }),
  });
}

export function useGroups(workspaceId: string) {
  return useQuery({
    queryKey: ['workspace', workspaceId, 'groups'],
    queryFn: () => unwrap(api.GET('/api/v1/workspaces/{workspaceId}/groups', { params: { path: { workspaceId } } })),
  });
}

export function useSaveGroup(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Schemas['GroupRequest'] & { id?: string }) =>
      id
        ? unwrap(
            api.PUT('/api/v1/workspaces/{workspaceId}/groups/{groupId}', {
              params: { path: { workspaceId, groupId: id } },
              body,
            }),
          )
        : unwrap(api.POST('/api/v1/workspaces/{workspaceId}/groups', { params: { path: { workspaceId } }, body })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['workspace', workspaceId, 'groups'] });
      void qc.invalidateQueries({ queryKey: ['workspace', workspaceId, 'members'] });
    },
  });
}

export function useDeleteGroup(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (groupId: string) =>
      unwrap(
        api.DELETE('/api/v1/workspaces/{workspaceId}/groups/{groupId}', { params: { path: { workspaceId, groupId } } }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workspace', workspaceId, 'groups'] }),
  });
}

export function useInvitation(token: string | undefined) {
  return useQuery({
    queryKey: ['invitation', token],
    queryFn: () => unwrap(api.GET('/api/v1/invitations/{token}', { params: { path: { token: token! } } })),
    enabled: !!token,
    retry: false,
  });
}

export function useAcceptInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (token: string) =>
      unwrap(api.POST('/api/v1/invitations/{token}/accept', { params: { path: { token } } })),
    onSuccess: () => qc.invalidateQueries(),
  });
}
